import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { SKIP_DUPLICATE_CHECK } from '@insite-store360/auth';
import { BehaviorSubject, catchError, Observable } from 'rxjs';
import { countryData, Region, Status, USER_URL, UserApiResponse } from '../shared/src';
import { ErrorHandlerService } from './error-handler.service';

interface PaginationParams {
  pageIndex: number;
  pageSize: number;
  searchFilter?: string;
  status?: number;
}

@Injectable({
  providedIn: 'root',
})
export class MasterserviceService {
  private readonly MASTER_API = `${USER_URL}`;
  private selectedUserSource = new BehaviorSubject<any>(null);
  selectedUser$ = this.selectedUserSource.asObservable();
  setSelectedUser(user: any) {
    this.selectedUserSource.next(user); // Update the selected user data
  }
  constructor(
    private http: HttpClient,
    private errorHandler: ErrorHandlerService
  ) { }

  // Reusable HTTP methods
  private getPaginated<T>(
    endpoint: string,
    params: PaginationParams
  ): Observable<T> {
    const queryParams = new URLSearchParams({
      pageIndex: params.pageIndex.toString(),
      pageSize: params.pageSize.toString(),
      ...(params.searchFilter && { searchFilter: params.searchFilter }),
      ...(params.status !== undefined && { status: params.status.toString() })
    });

    return this.http
      .get<T>(`${this.MASTER_API}/${endpoint}/getAll?${queryParams}`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public contryGetAll(
    pageIndex: number,
    pageSize: number,
    searchFilter?: string,
    status: number = 2,
  ): Observable<any> {
    return this.getPaginated('country', { pageIndex, pageSize, searchFilter, status });
  }

  public saveMasterCountry(countryData: any): Observable<any> {
    return this.http
      .post<any>(`${this.MASTER_API}/country/save`, countryData)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public deleteCountry(id: number): Observable<any> {
    return this.http
      .delete<any>(`${this.MASTER_API}/country/delete/${id}`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public updateCountry(countryData: any): Observable<any> {
    return this.http
      .put<any>(`${this.MASTER_API}/country/update`, countryData)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getCountryShortByCountryName(countryName: string): Observable<any> {
    return this.http
      .get<any>(`${this.MASTER_API}/country/getCountryShortByCountryName?countryName=${countryName}`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getTimeZone(search?: string): Observable<any> {
    let queryParams = new HttpParams();
    if (search) {
      queryParams = queryParams.set('search', search);
    }
    return this.http
      .get<any>(`${this.MASTER_API}/country/getTimeZone`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public registerRegion(admin: Region): Observable<Region> {
    return this.http
      .post<Region>(`${this.MASTER_API}/region/save`, admin)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getRegionDetails(
    pageIndex: number,
    pageSize: number,
    searchFilter?: string,
    status?: number
  ): Observable<any> {
    return this.getPaginated('region', { pageIndex, pageSize, searchFilter, status });
  }

  public updateRegionArea(data: Region): Observable<Region> {
    return this.http
      .post<Region>(`${this.MASTER_API}/region/update`, data)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public deleteRegionArea(id: number): Observable<any> {
    return this.http
      .delete<any>(`${this.MASTER_API}/region/delete/${id}`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getAllRoles(
    pageIndex: number,
    pageSize: number,
    searchFilter?: string,
    projectId?: number | null
  ): Observable<any> {
    let params = new HttpParams()
      .set('pageIndex', String(pageIndex))
      .set('pageSize', String(pageSize));
    if (searchFilter) {
      params = params.set('searchFilter', searchFilter);
    }
    if (projectId != null) {
      params = params.set('projectId', String(projectId));
    }
    return this.http
      .get<any>(`${this.MASTER_API}/role/getAll`, { params })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public saveRole(role: {
    id?: number;
    name: string;
    level?: number | null;
    projectId?: number | null;
    isMobileAccess?: boolean;
    isWebAccess?: boolean;
    status?: boolean;
  }): Observable<any> {
    return this.http
      .post<any>(`${this.MASTER_API}/role/save`, role)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public deleteRole(roleId: string): Observable<any> {
    return this.http
      .delete(`${this.MASTER_API}/role/delete/${roleId}`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public updateRole(role: {
    id: number;
    name: string;
    level?: number | null;
    projectId?: number | null;
    isMobileAccess?: boolean;
    isWebAccess?: boolean;
    status?: boolean;
  }): Observable<any> {
    return this.http
      .post<any>(`${this.MASTER_API}/role/update`, role)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public updateRoleToggleField(fieldName: string, id: number): Observable<any> {
    return this.http
      .put<any>(`${this.MASTER_API}/role/updateStatus/${fieldName}/${id}`, {})
      .pipe(catchError(this.errorHandler.handleError));
  }

  public importCountry(data: any): Observable<countryData[]> {
    return this.http
      .post<countryData[]>(`${this.MASTER_API}/country/importData`, data)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public importRegions(data: any): Observable<Region[]> {
    return this.http
      .post<Region[]>(`${this.MASTER_API}/region/importData`, data)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getRegionById(id: number): Observable<any> {
    return this.http
      .get<any>(`${this.MASTER_API}/country/getByRegionId/${id}`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getCountryFilter(ids?: number[], searchValue?: string): Observable<any> {
    if (ids !== undefined) {
      ids = Array.isArray(ids) ? ids : [ids];
    }
    let queryParams = new HttpParams();
    if (ids && Array.isArray(ids) && ids.length > 0) {
      queryParams = queryParams.set('ids', ids.join(','));
    }
    if (searchValue) {
      queryParams = queryParams.set('searchFilter', searchValue);
    }

    return this.http
      .get(`${this.MASTER_API}/country/getByRegionIds`, {
        params: queryParams,
        context: new HttpContext().set(SKIP_DUPLICATE_CHECK, true)
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  private exportData(endpoint: string, columns: string[]): Observable<Blob> {
    return this.http
      .post<Blob>(
        `${this.MASTER_API}/${endpoint}/downloadExcelReport`,
        { columns },
        { responseType: 'blob' as 'json' }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public exportCountries(): Observable<Blob> {
    return this.exportData('country', [
      'Country Name',
      'Short Name',
      'Country Code',
      'Region Name',
      'Language',
      'Currency',
      'Status',
      'Created By',
      'Created Date & Time',
      'Updated By',
      'Updated Date Time'
    ]);
  }

  public exportRegions(): Observable<Blob> {
    return this.exportData('region', ['Region Name', 'Country Name', 'Status', 'Created By', 'Created Date & Time', 'Updated By', 'Updated Date Time']);
  }

  public exportUsers(): Observable<Blob> {
    return this.exportData('user', [
      'First Name',
      'Last Name',
      'Role',
      'Email',
      'Phone Number',
      'Address',
      'DOB',
      'Gender',
      'Region',
      'Country',
      'Store',
    ]);
  }

  private toggleStatus(endpoint: string, status: Status): Observable<any> {
    return this.http
      .put<any>(
        `${this.MASTER_API}/${endpoint}/changeStatus/${status.id}`,
        status
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public toggleStatusRegion(adminStatus: Status): Observable<any> {
    return this.toggleStatus('region', adminStatus);
  }

  public toggleStatusCountry(adminStatus: Status): Observable<any> {
    return this.toggleStatus('country', adminStatus);
  }

  public updateCountryDynamicStatus(fieldName: string, id: number): Observable<any> {
    return this.http
      .put<any>(
        `${this.MASTER_API}/country/updateStatus/${fieldName}/${id}`,
        {}
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public updateCountryDistanceMeter(
    id: number,
    isCheckinValidation?: boolean | null,
    checkinDistanceMeter?: number | null,
    isCheckoutValidation?: boolean | null,
    checkoutDistanceMeter?: number | null
  ): Observable<any> {
    return this.http
      .put<any>(
        `${this.MASTER_API}/country/updateDistanceMeter/${id}`,
        {
          isCheckinValidation,
          checkinDistanceMeter,
          isCheckoutValidation,
          checkoutDistanceMeter
        }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public updateCountryGeneralSettings(
    id: number,
    settings: {
      isCheckInImageRequired?: boolean;
      isAbleToMapStore?: boolean;
      isCheckinValidation?: boolean;
      checkinDistanceMeter?: number | null;
      isCheckoutValidation?: boolean;
      checkoutDistanceMeter?: number | null;
      isAutoCheckout?: boolean;
      isPricePromotion?: boolean;
      isMultiReportingManager?: boolean;
      isIncDecBtnVisible?: boolean;
      correlationId?: string;
    }
  ): Observable<any> {
    return this.http
      .put<any>(
        `${this.MASTER_API}/general-settings/updateGeneralSettings/${id}`,
        settings
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Updates only the Price & Promotion toggle for a given country.
   * This is a convenience API that internally reuses the general-settings endpoint.
   */
  public updateCountryPricePromotion(
    id: number,
    isPricePromotion: boolean
  ): Observable<any> {
    return this.updateCountryGeneralSettings(id, { isPricePromotion });
  }

  public getGeneralSettingsAuditInfo(countryId: number): Observable<{
    lastUpdatedByName: string | null;
    lastUpdatedDateTime: Date | null;
  }> {
    return this.http
      .get<any>(`${this.MASTER_API}/general-settings/getGeneralSettingsAuditInfo/${countryId}`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getGeneralSettingsAuditHistory(params: {
    countryId: number;
    from?: string;
    to?: string;
    changedBy?: number;
    section?: string;
    settingKey?: string;
    page?: number;
    pageSize?: number;
  }): Observable<{
    total: number;
    items: Array<{
      id: number;
      correlationId: string | null;
      countryId: number;
      countryName: string;
      changedBy: number;
      changedByName: string;
      changedAt: string;
      changeSources: string[];
      changeCount: number;
      summary: string;
    }>;
  }> {
    let queryParams = new HttpParams().set('countryId', String(params.countryId));
    if (params.from) {
      queryParams = queryParams.set('from', params.from);
    }
    if (params.to) {
      queryParams = queryParams.set('to', params.to);
    }
    if (params.changedBy != null) {
      queryParams = queryParams.set('changedBy', String(params.changedBy));
    }
    if (params.section) {
      queryParams = queryParams.set('section', params.section);
    }
    if (params.settingKey) {
      queryParams = queryParams.set('settingKey', params.settingKey);
    }
    if (params.page != null) {
      queryParams = queryParams.set('page', String(params.page));
    }
    if (params.pageSize != null) {
      queryParams = queryParams.set('pageSize', String(params.pageSize));
    }
    return this.http
      .get<any>(`${this.MASTER_API}/general-settings/audit-history`, {
        params: queryParams,
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getGeneralSettingsAuditDetail(params: {
    eventId?: number;
    correlationId?: string;
  }): Observable<{
    id: number;
    correlationId: string | null;
    countryId: number;
    countryName: string;
    changedBy: number;
    changedByName: string;
    changedByEmail: string | null;
    changedAt: string;
    changeSources: string[];
    changes: Array<{
      settingKey: string;
      settingLabel: string;
      section: string;
      oldValue: string | null;
      newValue: string | null;
    }>;
  }> {
    const url = params.correlationId
      ? `${this.MASTER_API}/general-settings/audit-history/by-correlation/${params.correlationId}`
      : `${this.MASTER_API}/general-settings/audit-history/${params.eventId}`;
    return this.http
      .get<any>(url)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getSelloutSettings(countryId: number): Observable<any> {
    return this.http
      .get<any>(`${this.MASTER_API}/general-settings/getSelloutSettings/${countryId}`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public updateSelloutSettings(
    countryId: number,
    settings: {
      webDate: number;
      mobileDate: number;
      isPncVisible?: boolean;
      selloutWithoutCheckIn?: boolean;
      isSelloutIncreased?: boolean;
      isSelloutDecerease?: boolean;
      increasePrice?: number | null;
      decreasePrice?: number | null;
      isSelloutSerialNumber?: boolean;
      correlationId?: string;
    }
  ): Observable<any> {
    return this.http
      .put<any>(
        `${this.MASTER_API}/general-settings/updateSelloutSettings/${countryId}`,
        settings
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getDisplayComplianceSettings(countryId: number): Observable<any> {
    return this.http
      .get<any>(`${this.MASTER_API}/general-settings/getDisplayComplianceSettings/${countryId}`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public updateDisplayComplianceSettings(
    countryId: number,
    settings: {
      isProductOnDemoEnabled: boolean;
      categoryIds?: number[];
      correlationId?: string;
    }
  ): Observable<any> {
    return this.http
      .put<any>(
        `${this.MASTER_API}/general-settings/updateDisplayComplianceSettings/${countryId}`,
        settings
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public toggleStatusUser(adminStatus: Status): Observable<any> {
    return this.http
      .post<any>(
        `${this.MASTER_API}/user/updateStatus/${adminStatus.id}`,
        adminStatus
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public updateApprovalUser(userId: number, isApproval: boolean): Observable<any> {
    return this.http
      .post<any>(
        `${this.MASTER_API}/user/updateApproval/${userId}`,
        { isApproval }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public updatePmiAllowedUser(userId: number, isPmiAllowed: boolean): Observable<any> {
    return this.http
      .post<any>(
        `${this.MASTER_API}/user/updatePmiAllowed/${userId}`,
        { isPmiAllowed }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getAllRegionActiveStatus(searchValue?: string): Observable<any> {
    let queryParams = new HttpParams();
    if (searchValue) {
      queryParams = queryParams.set('searchFilter', searchValue);
    }
    return this.http
      .get<any>(`${this.MASTER_API}/region/getAllActive`, {
        params: queryParams,
        context: new HttpContext().set(SKIP_DUPLICATE_CHECK, true)
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getByCountryId(countryId: number): Observable<any> {
    console.log(countryId);
    return this.http
      .get<any>(`${this.MASTER_API}/region/getByCountryId/${countryId}`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getAllCountryActiveStatus(): Observable<any> {
    return this.http
      .get<any>(`${this.MASTER_API}/country/getAllActive`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  /** IntElux master countries by project (unscoped JWT country filter). Used by SPFT create-user. */
  public getCountryByProjectId(projectId: number): Observable<any> {
    return this.http
      .get<any>(`${this.MASTER_API}/country/getByProjectId/${projectId}`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getRegionByProjectId(projectId: number): Observable<any> {
    return this.http
      .get<any>(`${this.MASTER_API}/region/getByProjectId/${projectId}`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getAllCountry(
    pageIndex: number,
    pageSize: number,
    searchFilter?: string,
    status?: number
  ): Observable<any> {
    let queryParams = new HttpParams()
      .set('pageIndex', pageIndex)
      .set('pageSize', pageSize);

    if (searchFilter) {
      queryParams = queryParams.set('searchFilter', searchFilter);
    }

    if (status !== undefined) {
      queryParams = queryParams.set('status', status);
    }

    return this.http
      .get<any>(`${this.MASTER_API}/country/getAll`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getAllActiveCountryDetails(country?: string): Observable<any> {
    let queryParams = new HttpParams();
    if (country) {
      queryParams = queryParams.set('country', country);
    }
    return this.http
      .get<any>(`${this.MASTER_API}/country/getAllCountryData`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }


  public getAllfeatures(): Observable<any> {
    return this.http
      .get<any>(`${this.MASTER_API}/feature/getAll`)
      .pipe(catchError(this.errorHandler.handleError));
  }
  //Update user
  public updateUserData(userData: any): Observable<any> {
    return this.http
      .post(`${this.MASTER_API}/user/update`, userData)
      .pipe(catchError(this.errorHandler.handleError));
  }


  //loginid check
  public loginIdCheck(loginId: string): Observable<any> {
    return this.http
      .get(`${this.MASTER_API}/user/loginIdCheck?loginId=${loginId}`,)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public saveUserData(userData: any): Observable<any> {
    return this.http
      .post(`${this.MASTER_API}/user/save`, userData)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getCountryCode(): Observable<any> {
    return this.http
      .get(`${this.MASTER_API}/country/getAllCountyCode`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getCountryRMName(countryIds: any): Observable<any> {
    return this.http
      .post(`${this.MASTER_API}/user/getByRoleAndRegionIds`, countryIds)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public downloadExcelTemplate(): Observable<Blob> {
    return this.http
      .get(`${this.MASTER_API}/country/downloadExcelTemplate`, {
        responseType: 'blob',
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public excelRegionDownloadTemplate(): Observable<Blob> {
    return this.http
      .get(`${this.MASTER_API}/region/downloadExcelTemplate`, {
        responseType: 'blob',
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getRoleByLevel(id: number): Observable<any> {
    return this.http
      .get<any>(`${this.MASTER_API}/role/getByLevel/${id}`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getRoleByProjectId(projectId: number): Observable<any> {
    return this.http
      .get<any>(`${this.MASTER_API}/role/getByProjectId/${projectId}`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getStateByCountry(countryId: number): Observable<any> {
    return this.http.get<any>(`${this.MASTER_API}/state/getStateByCountry/${countryId}`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getStateByCountries(countryIds: string): Observable<any> {
    return this.http.get<any>(`${this.MASTER_API}/state/getStateByCountries?countryIds=${countryIds}`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getCityByState(stateId: number): Observable<any> {
    return this.http.get<any>(`${this.MASTER_API}/city/getCityByState/${stateId}`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public excelUsersDownloadTemplate(params: { roleIds: number[]; isCountryPIC: boolean }): Observable<Blob> {
    const { roleIds, isCountryPIC } = params;
    const rolesParam = roleIds.join(',');
    return this.http
      .get(`${this.MASTER_API}/user/downloadExcelTemplate?roles=${rolesParam}&isCountryPIC=${isCountryPIC}`, {
        responseType: 'blob',
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Imports a list of users into the server.
   * @param data - An array of users objects to be imported.
   * @returns An Observable that emits an array of imported users objects upon successful import.
   */
  public importUsers(data: Record<string, any>): Observable<any[]> {
    return this.http
      .post<any[]>(`${this.MASTER_API}/user/importData`, data)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public deleteUser(id: number): Observable<any> {
    return this.http
      .delete<any>(`${this.MASTER_API}/user/deleteUser/${id}`)
      .pipe(catchError(this.errorHandler.handleError));
  }
  // getAllStates

  public getAllStates(
    pageIndex: number,
    pageSize: number,
    country?: number[],
    searchFilter?: string
  ): Observable<any> {

    let queryParams = new HttpParams()
      .set('pageIndex', pageIndex)
      .set('pageSize', pageSize)
    if (searchFilter) {
      queryParams = queryParams.set('searchFilter', searchFilter);
    }
    if (country && country.length > 0) {
      queryParams = queryParams.set('country', country.join(','));
    }

    return this.http
      .get<any>(
        `${this.MASTER_API}/state/getAll`, { params: queryParams }
      ) // Include the data payload here
      .pipe(catchError(this.errorHandler.handleError));

  }
  // create state
  public addState(stateData: any): Observable<any> {
    return this.http
      .post<any>(`${this.MASTER_API}/state/save`, stateData)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getAllStateswithCountry(areaGroupIds: number): Observable<any> {
    console.log(areaGroupIds);

    return this.http
      .get<any>(
        `${this.MASTER_API}/state/getAllStateswithCountry/${areaGroupIds}`
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getAllActiveStates(): Observable<any> {
    return this.http
      .get<any>(`${this.MASTER_API}/state/getAllActive`)
      .pipe(catchError(this.errorHandler.handleError));
  }
  // /update state
  public updateStateData(stateData: any): Observable<any> {
    return this.http
      .put(`${this.MASTER_API}/state/update`, stateData)
      .pipe(catchError(this.errorHandler.handleError));
  }

  // delete State
  public deleteState(id: number): Observable<string> {
    return this.http
      .delete<string>(`${this.MASTER_API}/state/delete/${id}`)
      .pipe(catchError(this.errorHandler.handleError));
  }
  // toggle Status
  /**
   * Toggel status.
   * @param id - The unique identifier of the status
   */

  updateToggleStatus(id: number): Observable<any> {
    return this.http
      .put<any>(`${this.MASTER_API}/state/changeStatus/${id}`, null)
      .pipe(catchError(this.errorHandler.handleError));
  }

  // downloadExcelTempalte

  public downloadExcelStateTemplate(country: number): Observable<Blob> {
    let queryParams = new HttpParams()
    if (country) {
      queryParams = queryParams.set('country', country);
    }
    return this.http
      .get(`${this.MASTER_API}/state/downloadExcelTemplate`, {
        params: queryParams,
        responseType: 'blob',

      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  // importData

  public importStatesData(data: any): Observable<any[]> {
    return this.http
      .post<any[]>(`${this.MASTER_API}/state/importStateData`, data)
      .pipe(catchError(this.errorHandler.handleError));
  }

  // export

  public exportStates(country: number[], searchFilter: string): Observable<Blob> {

    let queryParams = new HttpParams()
    if (searchFilter) {
      queryParams = queryParams.set('searchFilter', searchFilter);
    }
    if (country && country.length > 0) {
      queryParams = queryParams.set('country', country.join(','));
    }
    const columns: string[] = ['State', 'Region', 'Country', 'Status', 'Created By', 'Created Date & Time', 'Updated By', 'Updated Date Time']; //export needed columns
    return this.http
      .post<Blob>(
        `${this.MASTER_API}/state/downloadExcelReport`,
        { columns },
        {
          params: queryParams,
          responseType: 'blob' as 'json',
        }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  // getAllCities

  public getAllCities(
    pageIndex: number,
    pageSize: number,
    country?: number[],
    searchFilter?: string
  ): Observable<any> {

    let queryParams = new HttpParams()
      .set('pageIndex', pageIndex)
      .set('pageSize', pageSize)
    if (searchFilter) {
      queryParams = queryParams.set('searchFilter', searchFilter);
    }
    if (country && country.length > 0) {
      queryParams = queryParams.set('country', country.join(','));
    }

    return this.http
      .get<any>(
        `${this.MASTER_API}/city/getAll`, { params: queryParams }
      ) // Include the data payload here
      .pipe(catchError(this.errorHandler.handleError));

  }

  // create city
  public addCities(stateData: any): Observable<any> {
    return this.http
      .post<any>(`${this.MASTER_API}/city/save`, stateData)
      .pipe(catchError(this.errorHandler.handleError));
  }

  // update cities
  public updateCitiesData(cityData: any): Observable<any> {
    return this.http
      .put(`${this.MASTER_API}/city/update`, cityData)
      .pipe(catchError(this.errorHandler.handleError));
  }

  // delete City
  public deleteCities(id: number): Observable<string> {
    return this.http
      .delete<string>(`${this.MASTER_API}/city/delete/${id}`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  // toggle Status
  /**
   * Toggel status.
   * @param id - The unique identifier of the status of city
   */

  updateCityToggleStatus(id: number): Observable<any> {
    return this.http
      .put<any>(`${this.MASTER_API}/city/changeStatus/${id}`, null)
      .pipe(catchError(this.errorHandler.handleError));
  }

  // downloadExcelTempalte

  public downloadExcelCitiesTemplate(country: number): Observable<Blob> {
    let queryParams = new HttpParams()
    if (country) {
      queryParams = queryParams.set('country', country);
    }
    return this.http
      .get(`${this.MASTER_API}/city/downloadExcelTemplate`, {
        params: queryParams,
        responseType: 'blob',
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  // importData

  public importCitiesdata(data: any): Observable<any[]> {
    return this.http
      .post<any[]>(`${this.MASTER_API}/city/importCityData`, data)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public exportcities(country: number[], searchFilter: string): Observable<Blob> {

    let queryParams = new HttpParams()
    if (searchFilter) {
      queryParams = queryParams.set('searchFilter', searchFilter);
    }
    if (country && country.length > 0) {
      queryParams = queryParams.set('country', country.join(','));
    }
    const columns: string[] = ['City', 'Country', 'Region', 'Status', 'Created By', 'Created Date & Time', 'Updated By', 'Updated Date Time']; //export needed columns
    return this.http
      .post<Blob>(
        `${this.MASTER_API}/city/downloadExcelReport`,
        { columns },
        {
          params: queryParams,
          responseType: 'blob' as 'json',
        }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  // getAllAreaGruop

  public getAllAreaGroups(
    pageIndex: number,
    pageSize: number,
    country?: number[],
    searchFilter?: string
  ): Observable<any> {
    let queryParams = new HttpParams()
      .set('pageIndex', pageIndex)
      .set('pageSize', pageSize)
    if (searchFilter) {
      queryParams = queryParams.set('searchFilter', searchFilter);
    }
    if (country && country.length > 0) {
      queryParams = queryParams.set('country', country.join(','));
    }
    return this.http
      .get<any>(
        `${this.MASTER_API}/areaGroup/getAll`, { params: queryParams }
      ) // Include the data payload here
      .pipe(catchError(this.errorHandler.handleError));

  }

  // create state
  public addAreas(AreaData: any): Observable<any> {
    return this.http
      .post<any>(`${this.MASTER_API}/areaGroup/save`, AreaData)
      .pipe(catchError(this.errorHandler.handleError));
  }

  // update Area
  public updateAreaData(AreaData: any): Observable<any> {
    return this.http
      .put(`${this.MASTER_API}/areaGroup/update`, AreaData)
      .pipe(catchError(this.errorHandler.handleError));
  }

  // delete Area
  public deleteAreaGruop(id: number): Observable<string> {
    return this.http
      .delete<string>(`${this.MASTER_API}/areaGroup/delete/${id}`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  // toggle Status
  /**
   * Toggel status.
   * @param id - The unique identifier of the status of area
   */

  updateAreaToggleStatus(id: number): Observable<any> {
    return this.http
      .put<any>(`${this.MASTER_API}/areaGroup/changeStatus/${id}`, null)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getAllActiveAreas(): Observable<any> {
    return this.http
      .get<any>(`${this.MASTER_API}/areaGroup/getAllActive`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getAreasByCountryId(countryIds: number[]): Observable<any> {
    const params = new HttpParams().set('countryId', countryIds.join(','));
    return this.http
      .get<any>(`${this.MASTER_API}/areaGroup/byCountryId`, { params })
      .pipe(catchError(this.errorHandler.handleError));
  }

  // downloadExcelTempalte

  public downloadExcelAreaTemplate(country: number): Observable<Blob> {
    let queryParams = new HttpParams()
    if (country) {
      queryParams = queryParams.set('country', country);
    }

    return this.http
      .get(`${this.MASTER_API}/areaGroup/downloadExcelTemplate`, {
        params: queryParams,
        responseType: 'blob',
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  // importData

  public importAreasdata(data: any): Observable<any[]> {
    return this.http
      .post<any[]>(`${this.MASTER_API}/areaGroup/importAreaData`, data)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public exportAres(country: number[], searchFilter: string): Observable<Blob> {

    let queryParams = new HttpParams()
    if (searchFilter) {
      queryParams = queryParams.set('searchFilter', searchFilter);
    }
    if (country && country.length > 0) {
      queryParams = queryParams.set('country', country.join(','));
    }
    const columns: string[] = ['Area', 'Region', 'Country', 'Status', 'Created By', 'Created Date & Time', 'Updated By', 'Updated Date Time']; //export needed columns
    return this.http
      .post<Blob>(
        `${this.MASTER_API}/areaGroup/downloadExcelReport`,
        { columns },
        {
          params: queryParams,
          responseType: 'blob' as 'json',
        }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getAllAreaWithCountries(countryId: number): Observable<any> {
    return this.http
      .get<any>(
        `${this.MASTER_API}/areaGroup/getAllAreasWithCountry/${countryId}`
      )
      .pipe(catchError(this.errorHandler.handleError));
  }



  public getAllStatesWithCity(stateId: number): Observable<any> {
    return this.http
      .get<any>(`${this.MASTER_API}/city/getAllStatesWithCity/${stateId}`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  // getCityByCountryId
  public getCityByCountryId(countryId: number): Observable<any> {
    return this.http
      .get<any>(`${this.MASTER_API}/city/getCityByCountryId/${countryId}`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  // create Language
  public addLanguage(LanguageData: any): Observable<any> {
    return this.http
      .post<any>(`${this.MASTER_API}/Language/save`, LanguageData)
      .pipe(catchError(this.errorHandler.handleError));
  }

  //get languages
  public getAllLanguages(params: {
    pageIndex: any,
    pageSize: any,
    searchFilter?: string,
    status: number
  }): Observable<UserApiResponse> {
    let queryParams = new HttpParams()
      .set('pageIndex', params.pageIndex)
      .set('pageSize', params.pageSize)
      .set('status', params.status);
    if (params.searchFilter) {
      queryParams = queryParams.set('searchFilter', params.searchFilter);
    }
    return this.http
      .get<UserApiResponse>(
        `${this.MASTER_API}/Language/getAll`, { params: queryParams }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  // update language
  public updateLanguageData(languageData: any): Observable<any> {
    return this.http
      .put(`${this.MASTER_API}/Language/update`, languageData)
      .pipe(catchError(this.errorHandler.handleError));
  }

  //change active status of the language
  updateLanguageToggleStatus(id: number): Observable<any> {
    return this.http
      .put<any>(`${this.MASTER_API}/Language/changeStatus/${id}`, null)
      .pipe(catchError(this.errorHandler.handleError));
  }

  //get active languages
  public getActiveLanguages(): Observable<any> {
    return this.http
      .get<UserApiResponse>(
        `${this.MASTER_API}/Language/getAllActive`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  // Language Label Management APIs
  public getAllLanguageLabels(params: {
    pageIndex: any,
    pageSize: any,
    searchFilter?: string,
    languageId?: number
  }): Observable<UserApiResponse> {
    let queryParams = new HttpParams()
      .set('pageIndex', params.pageIndex)
      .set('pageSize', params.pageSize);
    if (params.searchFilter) {
      queryParams = queryParams.set('searchFilter', params.searchFilter);
    }
    if (params.languageId) {
      queryParams = queryParams.set('languageId', params.languageId);
    }
    return this.http
      .get<UserApiResponse>(
        `${this.MASTER_API}/language-label/getAll`, { params: queryParams }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public createLanguageLabel(languageLabelData: any): Observable<any> {
    return this.http
      .post<any>(`${this.MASTER_API}/language-label/save`, languageLabelData)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public updateLanguageLabel(id: number, languageLabelData: any): Observable<any> {
    return this.http
      .put<any>(`${this.MASTER_API}/language-label/updateLabel/${id}`, languageLabelData)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public updateLanguageTranslation(id: number, translationData: any): Observable<any> {
    return this.http
      .put<any>(`${this.MASTER_API}/language-label/updateTranslation/${id}`, translationData)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public deleteLanguageLabel(id: number): Observable<any> {
    return this.http
      .delete<any>(`${this.MASTER_API}/language-label/deleteLabel/${id}`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public deleteLanguageTranslation(id: number): Observable<any> {
    return this.http
      .delete<any>(`${this.MASTER_API}/language-label/deleteTranslation/${id}`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public importLanguageLabelsMap(data: {
    languageId: number,
    labels: Record<string, string>,
    module?: string,
    overwrite?: boolean
  }): Observable<any> {
    return this.http
      .post<any>(`${this.MASTER_API}/language-label/importMap`, data)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getLanguageLabelsExportMap(languageId: number): Observable<UserApiResponse> {
    return this.http
      .get<UserApiResponse>(
        `${this.MASTER_API}/language-label/getAllLanguageLabelsForMobile/${languageId}`)
      .pipe(catchError(this.errorHandler.handleError));
  }
  public getSurveyResponsesDetails(): Observable<any> {
    return this.http
      .get<UserApiResponse>(
        `${this.MASTER_API}/user/surveyResponses`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getUnMappedFilterValue(params: { countryId?: number[], areaId?: number[], stateId?: number[] }): Observable<any> {
    let queryParams = new HttpParams()
    if (params.areaId && params.areaId.length > 0) {
      queryParams = queryParams.set('areaId', params.areaId.join(','))
    }
    if (params.stateId && params.stateId.length > 0) {
      queryParams = queryParams.set('stateId', params.stateId.join(','))
    }

    if (params.countryId && params.countryId?.length > 0) {
      queryParams = queryParams.set('countryId', params.countryId.join(','))
    }
    return this.http.get<any>(`${this.MASTER_API}/user/getUnMappedFilterValues`, { params: queryParams }).pipe(catchError(this.errorHandler.handleError));
  }

  public getMappedFilterValue(params: { countryId?: number[], areaId?: number[], stateId?: number[] }): Observable<any> {
    let queryParams = new HttpParams()
    if (params.areaId && params.areaId.length > 0) {
      queryParams = queryParams.set('areaId', params.areaId.join(','))
    }
    if (params.stateId && params.stateId.length > 0) {
      queryParams = queryParams.set('stateId', params.stateId.join(','))
    }
    if (params.countryId && params.countryId?.length > 0) {
      queryParams = queryParams.set('countryId', params.countryId.join(','))
    }
    return this.http.get<any>(`${this.MASTER_API}/user/getMappedFilterValues`, { params: queryParams }).pipe(catchError(this.errorHandler.handleError));
  }

  public getAllUnMappedStores(params: { pageIndex: number, pageSize: number, countryId: number[], userId: number, searchFilter?: string, areaId?: number[], stateId?: number[], cityId?: number[], accountId?: number[] }): Observable<any> {
    let queryParams = new HttpParams()
      .set('pageIndex', params.pageIndex)
      .set('pageSize', params.pageSize)
      .set('userId', params.userId)
    if (params.searchFilter) {
      queryParams = queryParams.set('searchFilter', params.searchFilter);
    }

    if (params.areaId && params.areaId.length > 0) {
      queryParams = queryParams.set('areaId', params.areaId.join(','))
    }
    if (params.stateId && params.stateId.length > 0) {
      queryParams = queryParams.set('stateId', params.stateId.join(','))
    }
    if (params.cityId && params.cityId.length > 0) {
      queryParams = queryParams.set('cityId', params.cityId.join(','))
    }
    if (params.accountId && params.accountId.length > 0) {
      queryParams = queryParams.set('accountId', params.accountId.join(','))
    }
    if (params.countryId && params.countryId.length > 0) {
      queryParams = queryParams.set('countryId', params.countryId.join(','))
    }
    return this.http
      .get<any>(
        `${this.MASTER_API}/user/getAllUnMappedStores`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getAllUnMappedStoresByTDS(params: { pageIndex: number, pageSize: number, countryId: number, userId: number, searchFilter?: string, areaId?: number[], stateId?: number[], cityId?: number[], accountId?: number[] }): Observable<any> {
    let queryParams = new HttpParams()
      .set('pageIndex', params.pageIndex)
      .set('pageSize', params.pageSize)
      .set('userId', params.userId)
    if (params.searchFilter) {
      queryParams = queryParams.set('searchFilter', params.searchFilter);
    }

    if (params.areaId && params.areaId.length > 0) {
      queryParams = queryParams.set('areaId', params.areaId.join(','))
    }
    if (params.stateId && params.stateId.length > 0) {
      queryParams = queryParams.set('stateId', params.stateId.join(','))
    }
    if (params.cityId && params.cityId.length > 0) {
      queryParams = queryParams.set('cityId', params.cityId.join(','))
    }
    if (params.accountId && params.accountId.length > 0) {
      queryParams = queryParams.set('accountId', params.accountId.join(','))
    }
    if (params.countryId) {
      queryParams = queryParams.set('countryId', params.countryId)
    }
    return this.http
      .get<any>(
        `${this.MASTER_API}/user/getAllUnMappedStoresByTDS`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }


  //To store the selected Value
  public addStoreMapping(data: { userId: number, storeId: number[] }): Observable<any> {
    return this.http.post<any>(`${this.MASTER_API}/user/storeMapping/save`, data).pipe(catchError(this.errorHandler.handleError));
  }

  //To remove the store
  public removeStoreMapping(data: { userId: number, storeId: number[] }): Observable<any> {
    return this.http.post<any>(`${this.MASTER_API}/user/storeMapping/remove`, data).pipe(catchError(this.errorHandler.handleError));
  }

  public getAllMappedStores(params: { pageIndex: number, pageSize: number, countryId: number, userId: number, searchFilter?: string, areaId?: number[], stateId?: number[], cityId?: number[], accountId?: number[] }): Observable<any> {
    let queryParams = new HttpParams()
      .set('pageIndex', params.pageIndex)
      .set('pageSize', params.pageSize)
      .set('userId', params.userId)

    if (params.searchFilter) {
      queryParams = queryParams.set('searchFilter', params.searchFilter);
    }

    if (params.areaId && params.areaId.length > 0) {
      queryParams = queryParams.set('areaId', params.areaId.join(','))
    }
    if (params.stateId && params.stateId.length > 0) {
      queryParams = queryParams.set('stateId', params.stateId.join(','))
    }
    if (params.cityId && params.cityId.length > 0) {
      queryParams = queryParams.set('cityId', params.cityId.join(','))
    }
    if (params.accountId && params.accountId.length > 0) {
      queryParams = queryParams.set('accountId', params.accountId.join(','))
    }
    if (params.countryId) {
      queryParams = queryParams.set('countryId', params.countryId)
    }
    return this.http
      .get<any>(
        `${this.MASTER_API}/user/getAllMappedStores`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getAllMappedStoresById(params: { searchFilter?: string }): Observable<any> {
    let queryParams = new HttpParams()

    if (params.searchFilter) {
      queryParams = queryParams.set('searchFilter', params.searchFilter);
    }
    return this.http
      .get<any>(
        `${this.MASTER_API}/user/getMappedStoreById`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public downloadStoreMappingTemplate(): Observable<Blob> {
    return this.http
      .get(`${this.MASTER_API}/user/storeMapping/downloadExcelTemplate`, {
        responseType: 'blob',
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
 * Imports a list of store into the server.
 * @param data - An array of store objects to be imported.
 * @returns An Observable that emits an array of imported store mapping objects upon successful import.
 */
  public importStoreMapping(data: Record<string, any>): Observable<any[]> {
    return this.http
      .post<any[]>(`${this.MASTER_API}/user/storeMapping/importData`, data)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getUserCalendarDetails(params: { month: number, year: number, countryId: number }): Observable<any> {
    let queryParams = new HttpParams()
    if (params.month) {
      queryParams = queryParams.set('month', params.month);
    }
    if (params.year) {
      queryParams = queryParams.set('year', params.year)
    }
    if (params.countryId) {
      queryParams = queryParams.set('countryId', params.countryId)
    }
    return this.http
      .get<any>(`${this.MASTER_API}/user/getUserCalendar`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public exportUserCalendar(params: { userType: number | undefined, monthYear: string, userSearch: string }): Observable<any> {
    let queryParams = new HttpParams()
    if (params.userType) {
      queryParams = queryParams.set('userType', params.userType);
    }
    if (params.monthYear) {
      queryParams = queryParams.set('monthYear', params.monthYear)
    }
    if (params.userSearch) {
      queryParams = queryParams.set('userSearch', params.userSearch)
    }
    return this.http
      .post<Blob>(
        `${this.MASTER_API}/user/userCalendar/downloadExcelReport`,
        {},
        {
          params: queryParams,
          responseType: 'blob' as 'json'
        }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public resetPassword(params: { userId: number, countryId: number }): Observable<any> {
    return this.http.post<any>(`${this.MASTER_API}/user/resetPassword`, params).pipe(catchError(this.errorHandler.handleError));
  }

  //to check the existing area validation
  public checkAreaGroupExists(areaGroupName: string[] | string, country: string): Observable<any> {
    let queryParams = new HttpParams();
    if (Array.isArray(areaGroupName)) {
      queryParams = queryParams.set('areaGroupName', areaGroupName.join(','));
    } else {
      queryParams = queryParams.set('areaGroupName', areaGroupName);
    }
    queryParams = queryParams.set('country', country.toString());
    return this.http
      .get<any>(`${this.MASTER_API}/areaGroup/checkAreaGroupExists`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }

  //to check the existing state validation
  public checkStateExists(data: any): Observable<any> {

    return this.http
      .post<any>(`${this.MASTER_API}/state/checkStateExists`, data)
      .pipe(catchError(this.errorHandler.handleError));
  }

  //to check the existing city validation
  public checkCityExists(data: any): Observable<any> {

    return this.http
      .post<any>(`${this.MASTER_API}/city/checkCityExists`, data)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getAllAreasWithCountry(countryId: number[]): Observable<any> {
    let queryParams = new HttpParams()
    if (countryId && countryId.length > 0) {
      queryParams = queryParams.set('countryId', countryId.join(','));
    }
    return this.http.get<any>(`${this.MASTER_API}/areaGroup/byCountryId`, { params: queryParams }).pipe(catchError(this.errorHandler.handleError));
  }

  public getAllStatesWithArea(areaId: number[]): Observable<any> {
    let queryParams = new HttpParams()
    if (areaId && areaId.length > 0) {
      queryParams = queryParams.set('areaId', areaId.join(','));
    }
    return this.http.get<any>(`${this.MASTER_API}/state/byAreaId`, { params: queryParams }).pipe(catchError(this.errorHandler.handleError));
  }

  public getStatesByCountryId(countryId: number[]): Observable<any> {
    let queryParams = new HttpParams()
    if (countryId && countryId.length > 0) {
      queryParams = queryParams.set('countryId', countryId.join(','));
    }
    return this.http.get<any>(`${this.MASTER_API}/state/byCountryId`, { params: queryParams }).pipe(catchError(this.errorHandler.handleError));
  }

  // FAQs APIs
  public getAllFaqs(params: {
    pageIndex: number,
    pageSize: number,
    searchFilter?: string
  }): Observable<any> {
    let queryParams = new HttpParams()
      .set('pageIndex', params.pageIndex.toString())
      .set('pageSize', params.pageSize.toString());
    if (params.searchFilter) {
      queryParams = queryParams.set('searchFilter', params.searchFilter);
    }
    return this.http
      .get<any>(`${this.MASTER_API}/faqs/getAll`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getFaqById(id: number): Observable<any> {
    return this.http
      .get<any>(`${this.MASTER_API}/faqs/getById/${id}`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public addFaq(faqData: any): Observable<any> {
    return this.http
      .post<any>(`${this.MASTER_API}/faqs/save`, faqData)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public updateFaq(faqData: any): Observable<any> {
    return this.http
      .put<any>(`${this.MASTER_API}/faqs/update`, faqData)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public deleteFaq(id: number): Observable<any> {
    return this.http
      .delete<any>(`${this.MASTER_API}/faqs/delete/${id}`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  // Guide Material APIs
  public getAllGuideMaterials(params: {
    pageIndex: number,
    pageSize: number,
    searchFilter?: string
  }): Observable<any> {
    let queryParams = new HttpParams()
      .set('pageIndex', params.pageIndex.toString())
      .set('pageSize', params.pageSize.toString());
    if (params.searchFilter) {
      queryParams = queryParams.set('searchFilter', params.searchFilter);
    }
    return this.http
      .get<any>(`${this.MASTER_API}/guide-material/getAll`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getGuideMaterialById(id: number): Observable<any> {
    return this.http
      .get<any>(`${this.MASTER_API}/guide-material/getById/${id}`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public addGuideMaterial(guideMaterialData: any): Observable<any> {
    return this.http
      .post<any>(`${this.MASTER_API}/guide-material/save`, guideMaterialData)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public updateGuideMaterial(guideMaterialData: any): Observable<any> {
    return this.http
      .put<any>(`${this.MASTER_API}/guide-material/update`, guideMaterialData)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public deleteGuideMaterial(id: number): Observable<any> {
    return this.http
      .delete<any>(`${this.MASTER_API}/guide-material/delete/${id}`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  // Ticket APIs
  public getAllTickets(params: {
    pageIndex?: number,
    pageSize?: number,
    searchFilter?: string,
    status?: string,
    priority?: string,
    createdBy?: number
  }): Observable<any> {
    let queryParams = new HttpParams();
    if (params.pageIndex) {
      queryParams = queryParams.set('pageIndex', params.pageIndex.toString());
    }
    if (params.pageSize) {
      queryParams = queryParams.set('pageSize', params.pageSize.toString());
    }
    if (params.searchFilter) {
      queryParams = queryParams.set('searchFilter', params.searchFilter);
    }
    if (params.status) {
      queryParams = queryParams.set('status', params.status);
    }
    if (params.priority) {
      queryParams = queryParams.set('priority', params.priority);
    }
    if (params.createdBy) {
      queryParams = queryParams.set('createdBy', params.createdBy.toString());
    }
    return this.http
      .get<any>(`${this.MASTER_API}/ticket/getAll`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getTicketById(id: number): Observable<any> {
    return this.http
      .get<any>(`${this.MASTER_API}/ticket/getById/${id}`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public createTicket(ticketData: any): Observable<any> {
    return this.http
      .post<any>(`${this.MASTER_API}/ticket/save`, ticketData)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public createTicketWithFiles(formData: FormData): Observable<any> {
    return this.http
      .post<any>(`${this.MASTER_API}/ticket/save`, formData)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public updateTicket(ticketData: any): Observable<any> {
    return this.http
      .put<any>(`${this.MASTER_API}/ticket/update`, ticketData)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public updateTicketStatus(id: number, status: string): Observable<any> {
    return this.http
      .put<any>(`${this.MASTER_API}/ticket/updateStatus/${id}`, { status })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public deleteTicket(id: number): Observable<any> {
    return this.http
      .delete<any>(`${this.MASTER_API}/ticket/delete/${id}`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  // Ticket Reply APIs
  public createTicketReply(replyData: any): Observable<any> {
    return this.http
      .post<any>(`${this.MASTER_API}/ticket-reply/save`, replyData)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getTicketRepliesByTicketId(ticketId: number): Observable<any> {
    return this.http
      .get<any>(`${this.MASTER_API}/ticket-reply/getByTicketId/${ticketId}`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public deleteTicketReply(id: number): Observable<any> {
    return this.http
      .delete<any>(`${this.MASTER_API}/ticket-reply/delete/${id}`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getAllTicketControls(params: {
    pageIndex?: number,
    pageSize?: number,
    searchFilter?: string
  }): Observable<any> {
    let queryParams = new HttpParams();
    if (params.pageIndex) {
      queryParams = queryParams.set('pageIndex', params.pageIndex.toString());
    }
    if (params.pageSize) {
      queryParams = queryParams.set('pageSize', params.pageSize.toString());
    }
    if (params.searchFilter) {
      queryParams = queryParams.set('searchFilter', params.searchFilter);
    }
    return this.http
      .get<any>(`${this.MASTER_API}/ticket-control/getAll`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }


  public deleteTicketControl(id: number): Observable<any> {
    return this.http
      .delete<any>(`${this.MASTER_API}/ticket-control/delete/${id}`)
      .pipe(catchError(this.errorHandler.handleError));
  }


  public addTicketControl(ticketControlData: any): Observable<any> {
    return this.http
      .post<any>(`${this.MASTER_API}/ticket-control/save`, ticketControlData)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getQuizMandatoryDropdownUsers(countryId: number, roleIds: number[]): Observable<any> {
    const queryParams = new HttpParams()
      .set('countryId', String(countryId))
      .set('roleIds', roleIds.join(','));
    return this.http
      .get<any>(`${this.MASTER_API}/quiz-mandatory-control/dropdown-users`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getAllQuizMandatoryControl(params: {
    pageIndex?: number;
    pageSize?: number;
    searchValue?: string;
    countryIds?: number[];
    roleId?: number;
  }): Observable<any> {
    let queryParams = new HttpParams();
    if (params.pageIndex) {
      queryParams = queryParams.set('pageIndex', params.pageIndex.toString());
    }
    if (params.pageSize) {
      queryParams = queryParams.set('pageSize', params.pageSize.toString());
    }
    if (params.searchValue) {
      queryParams = queryParams.set('searchValue', params.searchValue);
    }
    if (params.countryIds && params.countryIds.length > 0) {
      queryParams = queryParams.set('countryIds', params.countryIds.join(','));
    }
    if (params.roleId) {
      queryParams = queryParams.set('roleId', params.roleId.toString());
    }
    return this.http
      .get<any>(`${this.MASTER_API}/quiz-mandatory-control/getAll`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public deleteQuizMandatoryControl(id: number): Observable<any> {
    return this.http
      .delete<any>(`${this.MASTER_API}/quiz-mandatory-control/delete/${id}`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public saveQuizMandatoryControl(data: {
    userId: number;
    countryId: number;
    roleId: number;
  }): Observable<any> {
    return this.http
      .post<any>(`${this.MASTER_API}/quiz-mandatory-control/save`, data)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public updateTicketControl(ticketControlData: any): Observable<any> {
    return this.http
      .put<any>(`${this.MASTER_API}/ticket-control/update`, ticketControlData)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public updateToggleTicketControlStatus(id: number): Observable<any> {
    return this.http
      .put<any>(`${this.MASTER_API}/ticket-control/changeStatus/${id}`, null)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public checkTicketControlAccess(userId: number): Observable<any> {
    return this.http
      .get<any>(`${this.MASTER_API}/ticket-control/checkAccess/${userId}`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getAppMaintenanceSetting(): Observable<any> {
    return this.http
      .get<any>(`${this.MASTER_API}/settings/getAppMaintenanceSetting`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public updateAppMaintenanceSetting(payload: {
    isUnderMaintenance: boolean;
    showAdvanceNotice: boolean;
    maintenanceMessage?: string;
    noticeMessage?: string;
  }): Observable<any> {
    return this.http
      .put<any>(`${this.MASTER_API}/settings/updateAppMaintenanceSetting`, payload)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getMobileAppVersions(): Observable<any> {
    return this.http
      .get<any>(`${this.MASTER_API}/settings/getMobileAppVersions`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public updateMobileAppVersion(payload: {
    id: number;
    buildVersion: string;
    buildNumber: string;
  }): Observable<any> {
    return this.http
      .put<any>(`${this.MASTER_API}/settings/updateMobileAppVersion`, payload)
      .pipe(catchError(this.errorHandler.handleError));
  }
}
